import React, { FC, useCallback, useEffect, useState } from 'react';
import Dropzone, { FileRejection } from 'react-dropzone';
import { toast } from 'react-toastify';
import API from 'api/API';
import { uploadTrayReferenceImageForDraft } from 'api/Endpoints/SetEndpoint';
import { SetDraftResponse } from 'api/Responses/SetResponses';
import AsyncImage from 'components/AsyncImage/AsyncImage';
import Checkbox from 'components/Checkbox/Checkbox';
import { FileUploadRoot } from 'components/FileUpload/FileUpload.styles';
import FormControlLabel from 'components/FormControl/FormControlLabel';
import InputField from 'components/InputField';
import Label from 'components/Label/Label';
import ReferenceImageGeneratorModal from 'components/ReferenceImageGenerator';
import { Tag } from 'components/ReferenceImageGenerator/molecules/ImageGeneratorStage';
import { useLanguage } from 'context/AppContext';
import useUploadTrayReferenceImage from 'hooks/useUploadTrayReferenceImage';
import { draftActions, SetAction } from 'reducers/DraftReducer/draftActions';
import { getSrc, StorageType, uploadAndSaveImage } from 'utils/helpers/image.helpers';
import { ImageContainer, TrayAttributesContainer, TrayDetailsContainer, TrayFileUploader } from './TrayDetails.styles';
import { Grid } from '@mui/material';

interface TrayDetailsProps {
  set: SetDraftResponse;
  trayId: string;
  dispatchDraft: React.Dispatch<SetAction>;
  disabled: boolean;
  isViewOnly: boolean;
}

const TrayDetails: FC<TrayDetailsProps> = ({ set, trayId, dispatchDraft, disabled, isViewOnly }) => {
  const tray = set.trays[trayId];
  const [language] = useLanguage();
  const [openImageEditor, setOpenImageEditor] = useState<boolean>(false);
  const [updateImage, setUpdateImage] = useState<number>(1);
  const [imageUrl, setImageUrl] = useState<string>();
  const { onSaveTaggedImageToDraft } = useUploadTrayReferenceImage(set, tray);

  /*
   * If the UI is view only, the image_file src is correct to grab from GCS
   * however if view only is false, the src has to be edited to grab the correct image via BE endpoint
   */

  const onSaveTaggedImage = useCallback(
    async (tags: Tag[], taggedImage: string, originalImage: string) => {
      try {
        const imagePath = await onSaveTaggedImageToDraft(taggedImage);
        dispatchDraft(draftActions.trayImage(imagePath, originalImage, tags, trayId));
        setOpenImageEditor(false);
        setUpdateImage(updateImage + 1);
        toast.success(language.AddModal.Tray.annotateSuccess);
      } catch (error) {
        API.errorHandler(error);
      }
    },
    [dispatchDraft, language.AddModal.Tray.annotateSuccess, onSaveTaggedImageToDraft, trayId, updateImage],
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[], error: FileRejection[]) => {
      // If any errors exist, log them
      if (error.length > 0) {
        toast.error(error[0].errors[0].message);
      }
      // Otherwise, upload the requested image
      else {
        try {
          // save to draft bucket
          const imageResponse = await uploadTrayReferenceImageForDraft(set.code, acceptedFiles[0]);

          await uploadAndSaveImage(set, trayId, imageResponse.image_path);
          // Reflect changes in the frontend
          dispatchDraft(draftActions.trayImage(imageResponse.image_path, imageResponse.image_path, [], trayId));
          // Re-render image
          setUpdateImage(updateImage + 1);
          // Log successful upload
          toast.success(language.AddModal.Tray.uploadSuccess);
        } catch (error) {
          API.errorHandler(error);
        }
      }
    },
    [set, dispatchDraft, trayId, updateImage, language.AddModal.Tray.uploadSuccess],
  );

  const handleChangeRef = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      dispatchDraft(draftActions.trayRef(e.target.value, trayId));
    },
    [dispatchDraft, trayId],
  );

  const handleChangeName = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      dispatchDraft(draftActions.trayName(e.target.value, trayId));
    },
    [dispatchDraft, trayId],
  );

  const handleChangeAdditionalRequirements = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      dispatchDraft(draftActions.trayAdditionalInspection(e.target.value, trayId));
    },
    [dispatchDraft, trayId],
  );

  const handleChangeTrayNonStandard = useCallback(() => {
    dispatchDraft(draftActions.trayStandard(!tray.non_standard, trayId));
  }, [dispatchDraft, tray.non_standard, trayId]);

  const handleChangeTrayGeneralInspection = useCallback(() => {
    dispatchDraft(draftActions.trayGeneralTest(!tray.general_test, trayId));
  }, [dispatchDraft, tray.general_test, trayId]);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const resolvedSrc = await getSrc(StorageType.DRAFT, '', set.code, tray.reference);
        setImageUrl(resolvedSrc);
      } catch (error) {
        API.errorHandler(error);
      }
    };
    void fetchImage();
  }, [set.code, tray.reference, updateImage]);

  return (
    <TrayDetailsContainer>
      <ImageContainer>
        <AsyncImage
          src={tray.image_file}
          setCode={set.code}
          trayRef={tray.reference}
          alt={tray.reference}
          updateTrigger={updateImage}
          openAnnotateImage={!disabled ? () => setOpenImageEditor(true) : undefined}
          isEnlargedImageAvailable
          imgWidth={19}
          imgHeight={14}
          editable={!disabled}
          storageType={!disabled ? StorageType.DRAFT : StorageType.FIREBASE}
        />
      </ImageContainer>
      {!disabled && (
        <Dropzone onDrop={onDrop} accept="image/*" maxFiles={1} disabled={disabled}>
          {({ getRootProps, getInputProps }) => (
            <TrayFileUploader>
              <FileUploadRoot {...getRootProps()}>
                <input {...getInputProps()} />
                <p>{language.TrayTable.upload}</p>
              </FileUploadRoot>
            </TrayFileUploader>
          )}
        </Dropzone>
      )}

      <TrayAttributesContainer>
        <Label text={language.TrayTable.reference}>
          <InputField variant="outlined" fullWidth value={tray.reference} onChange={handleChangeRef} disabled />
        </Label>
        <Label text={language.TrayTable.name}>
          <InputField variant="outlined" fullWidth value={tray.name} onChange={handleChangeName} disabled={disabled} />
        </Label>
        <Label text={language.TrayTable.notes}>
          <InputField
            variant="outlined"
            fullWidth
            value={tray.additional_inspection_requirements}
            onChange={handleChangeAdditionalRequirements}
            disabled={disabled}
          />
        </Label>
        <Grid container spacing={2}>
          <FormControlLabel
            control={
              <Checkbox checked={tray.non_standard} onChange={handleChangeTrayNonStandard} disabled={disabled} />
            }
            label={language.TrayTable.nonStandard}
          />
          <FormControlLabel
            control={
              <Checkbox checked={tray.general_test} onChange={handleChangeTrayGeneralInspection} disabled={disabled} />
            }
            label={language.TrayTable.generalInspection}
          />
        </Grid>
      </TrayAttributesContainer>
      <ReferenceImageGeneratorModal
        imageUrl={imageUrl}
        modalOpen={openImageEditor}
        setClosed={() => setOpenImageEditor(false)}
        originalImage={tray.original_image_file ?? tray.image_file ?? ''}
        existingTags={tray.reference_tags_metadata}
        onSaveTaggedImage={onSaveTaggedImage}
      />
    </TrayDetailsContainer>
  );
};

const MemoizedTrayDetails = React.memo(TrayDetails);

export default MemoizedTrayDetails;
